/**
 * Whole UTC days as `YYYY-MM-DD` strings (docs/04 section 10, ADR-0009 #9). Billboards are booked
 * by UTC day, so every calculation here works on calendar dates in UTC and never on the visitor's
 * local time zone. "Today" always comes from the server (`BillboardListResponse.today`) when it is
 * known; the local clock is only a fallback for timers.
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** Epoch milliseconds of 00:00 UTC on a `YYYY-MM-DD` date, or null when the string is not a real date. */
export function utcDayStart(date: string): number | null {
  const m = DATE_RE.exec(date);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  // Rejects 2026-02-30 and similar (Date.UTC would roll them over).
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
  return t;
}

export function isUtcDate(date: string): boolean {
  return utcDayStart(date) !== null;
}

/** The UTC calendar date of an epoch-ms instant. */
export function utcDateOf(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

/** `date` plus `days` whole UTC days (negative to go back). Throws on an invalid date. */
export function addUtcDays(date: string, days: number): string {
  const t = utcDayStart(date);
  if (t === null) throw new Error(`invalid UTC date: ${date}`);
  return utcDateOf(t + days * DAY_MS);
}

/** Whole days from `a` to `b` (b - a); positive when b is later. */
export function utcDaysBetween(a: string, b: string): number {
  const ta = utcDayStart(a);
  const tb = utcDayStart(b);
  if (ta === null || tb === null) throw new Error(`invalid UTC date: ${ta === null ? a : b}`);
  return Math.round((tb - ta) / DAY_MS);
}

export function compareUtcDates(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** The days of [start, start + count), in order. */
export function utcDayRange(start: string, count: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(addUtcDays(start, i));
  return out;
}

/** Milliseconds from `nowMs` until the next 00:00 UTC (always > 0; exactly at midnight it is a full day). */
export function msUntilNextUtcMidnight(nowMs: number): number {
  const next = (Math.floor(nowMs / DAY_MS) + 1) * DAY_MS;
  return next - nowMs;
}

/** 0 = Monday ... 6 = Sunday (ISO weekday order, used by the calendar grid). */
export function isoWeekdayIndex(date: string): number {
  const t = utcDayStart(date);
  if (t === null) throw new Error(`invalid UTC date: ${date}`);
  return (new Date(t).getUTCDay() + 6) % 7;
}

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const WEEKDAY_LABELS: readonly string[] = WEEKDAY_SHORT;

/** "Mon 12 Oct 2026" (a UTC date, written the same way for every visitor). */
export function formatUtcDay(date: string): string {
  const t = utcDayStart(date);
  if (t === null) return date;
  const d = new Date(t);
  return `${WEEKDAY_SHORT[isoWeekdayIndex(date)]} ${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "October 2026" for a `YYYY-MM` month key. */
export function formatUtcMonth(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return monthKey;
  return `${MONTH_LONG[m - 1]} ${y}`;
}

/**
 * A booked period written for people: the last day is shown (the API's end date is exclusive).
 * "Mon 12 Oct 2026 – Sun 18 Oct 2026 (7 days, UTC)".
 */
export function formatUtcPeriod(startDate: string, endExclusive: string): string {
  if (!isUtcDate(startDate) || !isUtcDate(endExclusive)) return `${startDate} – ${endExclusive}`;
  const days = utcDaysBetween(startDate, endExclusive);
  if (days <= 0) return `${formatUtcDay(startDate)} (UTC)`;
  const last = addUtcDays(endExclusive, -1);
  return `${formatUtcDay(startDate)} – ${formatUtcDay(last)} (${days} day${days === 1 ? "" : "s"}, UTC)`;
}

/** "1 Oct 2026" (no weekday), the same for every visitor and browser. */
export function formatUtcDateShort(date: string): string {
  const t = utcDayStart(date);
  if (t === null) return date;
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
